import * as React from 'react';
import {
  DownOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import {
  Button,
  Dropdown,
  Empty,
  Input,
  Menu,
  message,
  notification,
  Popconfirm,
  Space,
  Table,
  Tag,
} from 'antd';
import CreateBudget from './CreateBudget/CreateBudget';
import {
  GetBudgetConfigQuery,
  GetBudgetDocument,
  GetBudgetQuery,
  GetBudgetQueryVariables,
  GetBudgetsDocument,
  GetBudgetsQuery,
  UpdateBudgetCustomDocument,
  useDeleteABudgetMutation,
  useGetAllClientsQuery,
  useGetBudgetConfigQuery,
  useGetBudgetsQuery,
  useOnNewBudgetSubscriptionCustomSubscription,
  useOnUpdateBudgetSubscriptionCustomSubscription,
} from '../../graphql/datamodel.gen';
import { getClient } from '../../apollo';
import { formatDate } from '../../utils/date';
import moment from 'moment';
import { capitalizeFirstChar } from '../../utils/string';
import { computeBudgetTotalWithVAT } from './utils';
import { instantiateBudgetFromNetworkResponse } from './CreateItinerary/utils';
import EditBudget from './EditBudget/EditBudget';
import { BudgetInstance } from './CreateBudget/CreateBudgetForm';
import { ClientWithoutServices } from '../clients/types';
import CreateBudgetPDF from './pdf';
import Highlighter from 'react-highlight-words';
import _ from 'lodash';
import { NetworkStatus } from '@apollo/client';
import printJS from 'print-js';
import ReactToPrint from 'react-to-print';
import { useContext } from 'react';
import { IContext, MainContext } from '../../contexts/mainContext';
import { SortOrder } from 'antd/lib/table/interface';
import { sleep } from '../../utils/async';

interface IBudgetsProps {}

export type Budget = GetBudgetsQuery['budgets'][number];
type BudgetsConfig = GetBudgetConfigQuery['budgetConfig'];

const openNotificationNewBudget = () => {
  notification.open({
    message: 'Nuevo presupuesto',
    description: 'Un nuevo presupuesto ha sido creado en el sistema',
  });
};

const Budgets: React.FC<IBudgetsProps> = (props: IBudgetsProps) => {
  const { isClient, isAdmin } = useContext<IContext>(MainContext);

  useOnUpdateBudgetSubscriptionCustomSubscription({
    client: getClient(),
    shouldResubscribe: false,
    onSubscriptionData: ({ subscriptionData }) => {
      const client = getClient();

      client.writeQuery({
        query: UpdateBudgetCustomDocument,
        data: subscriptionData.data?.onUpdateBudget,
        id: subscriptionData.data?.onUpdateBudget?.id,
      });
    },
  });

  useOnNewBudgetSubscriptionCustomSubscription({
    client: getClient(),
    shouldResubscribe: false,
    onSubscriptionData: ({ subscriptionData }) => {
      const client = getClient();

      try {
        const previous = client.readQuery<GetBudgetsQuery>({
          query: GetBudgetsDocument,
        });

        const previousClone = _.cloneDeep(previous);

        const data = {
          budgets: [...previousClone.budgets, subscriptionData.data.newBudget],
        };
        client.writeQuery({
          query: GetBudgetsDocument,
          data,
        });
        // @ts-ignore
        client.queryManager.broadcastQueries();
        openNotificationNewBudget();
      } catch (e) {}
    },
  });

  const {
    data: dataClients,
    error: errorClients,
    networkStatus: networkStatusAllClients,
    loading: loadingClients,
  } = useGetAllClientsQuery({
    client: getClient(),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: dataBudgets,
    networkStatus: networkStatusBudgets,
    refetch: refetchBudgets,
    loading: loadingBudgets,
  } = useGetBudgetsQuery({
    client: getClient(),
    returnPartialData: true,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: dataBudgetsConfig,
    networkStatus: networkStatusBudgetConfig,
    loading: loadingBudgetConfigQuery,
  } = useGetBudgetConfigQuery({
    client: getClient(),
    notifyOnNetworkStatusChange: true,
  });

  const deleteBudgetMutation = useDeleteABudgetMutation({
    client: getClient(),
  });

  const _loadingBudgets =
    (loadingBudgets && !dataBudgets) ||
    networkStatusBudgets === NetworkStatus.refetch;

  const _loadingAllClients =
    (loadingClients && !dataClients) ||
    networkStatusAllClients === NetworkStatus.refetch;
  const _loadingBudgetsConfig =
    (loadingBudgetConfigQuery && !dataBudgetsConfig) ||
    networkStatusBudgetConfig === NetworkStatus.refetch;

  const loading =
    _loadingBudgets || _loadingAllClients || _loadingBudgetsConfig;
  const budgets = dataBudgets?.budgets || [];
  const budgetsConfig = dataBudgetsConfig?.budgetConfig;
  const allClients = dataClients?.clients || [];

  const instantiatedBudgets = (budgets || []).map(
    instantiateBudgetFromNetworkResponse,
  );

  const deleteBudget = (budget: string) => {
    deleteBudgetMutation[0]({
      variables: {
        id: budget,
      },
      onCompleted: () => {
        message.success('Presupuesto eliminado correctamente');
        refetchBudgets();
      },
      onError: () => {
        message.error('Error al eliminar presupuesto');
      },
    });
  };

  return (
    <BudgetsView
      isAdmin={isAdmin}
      isClient={isClient}
      allClients={[...allClients]}
      budgets={instantiatedBudgets}
      budgetsConfig={budgetsConfig}
      loading={loading}
      refetchBudgets={refetchBudgets}
      deleteBudget={deleteBudget}
    />
  );
};

interface IBudgetsViewProps {
  isClient: boolean;
  isAdmin: boolean;
  allClients: ClientWithoutServices[];
  budgets: BudgetInstance[];
  budgetsConfig: BudgetsConfig;
  loading: boolean;
  refetchBudgets: () => any;
  deleteBudget: (budget: string) => void;
}
interface IBudgetsViewState {
  createBudgetDrawerVisible: boolean;
  editBudgetDrawerVisible: boolean;
  selectedBudgetId?: string;
  selectedBudget?: GetBudgetQuery['budget'];
  print: boolean;
  searchText?: string;
  filteredInfo: object;
  sortedInfo: object;
}

class BudgetsView extends React.Component<
  IBudgetsViewProps,
  IBudgetsViewState
> {
  state = {
    createBudgetDrawerVisible: false,
    editBudgetDrawerVisible: false,
    selectedBudgetId: undefined,
    selectedBudget: undefined,
    print: false,
    searchText: undefined,
    filteredInfo: {},
    sortedInfo: {},
  };

  searchInput: any;
  componentToPrintRef: CreateBudgetPDF | null = null;
  reactPrintRef: ReactToPrint | null = null;

  constructor(props: Readonly<IBudgetsViewProps>) {
    super(props);
  }

  setComponentRef = (ref: CreateBudgetPDF) => {
    this.componentToPrintRef = ref;
  };

  openCreateBudgetDrawer = () => {
    this.setState({
      createBudgetDrawerVisible: true,
    });
  };

  hideCreateBudgetDrawer = () => {
    this.setState({
      createBudgetDrawerVisible: false,
    });
  };

  onCreateBudgetDrawerCloseClicked = () => {
    this.hideCreateBudgetDrawer();
  };

  openEditBudgetDrawer = (budgetId: string) => {
    this.setState({
      selectedBudgetId: budgetId,
      editBudgetDrawerVisible: true,
    });
  };

  hideEditBudgetDrawer = () => {
    this.setState({
      editBudgetDrawerVisible: false,
      selectedBudgetId: undefined,
    });
  };

  onEditBudgetDrawerCloseClicked = () => {
    this.hideEditBudgetDrawer();
  };

  printDiv(divName) {
    var prtContent = document.getElementById(divName);
    var WinPrint = window.open(
      '',
      '',
      'left=0,top=0,width=0,height=0,toolbar=0,scrollbars=0,status=0',
    );
    WinPrint.document.write(
      '<link rel="stylesheet" type="text/css" media="print" href="https://stackpath.bootstrapcdn.com/bootstrap/4.5.0/css/bootstrap.min.css" integrity="sha384-9aIt2nRpC12Uk9gS9baDl411NQApFmC26EwAOH8WgZl5MYYxFfc+NcPb1dKGj7Sk" crossOrigin="anonymous">',
    );
    WinPrint.document.write(
      '<script src="https://stackpath.bootstrapcdn.com/bootstrap/4.5.0/js/bootstrap.min.js" integrity="sha384-OgVRvuATP1z7JjHLkuOU7Xw704+h835Lr+6QL9UvYjZE3Ipu6Tp75j7Bh/kR0JKI" crossOrigin="anonymous">',
    );
    WinPrint.document.write(prtContent.innerHTML);
    WinPrint.document.close();
    setTimeout(() => {
      WinPrint.focus();
      WinPrint.print();
      WinPrint.close();
      this.setState({
        print: false,
        selectedBudgetId: undefined,
      });
    }, 1000);

    // WinPrint.close();
  }

  componentDidUpdate(
    prevProps: Readonly<IBudgetsViewProps>,
    prevState: Readonly<IBudgetsViewState>,
    snapshot?: any,
  ): void {
    if (this.state.print && !prevState.print) {
      // printJS('budget-pdf', 'html');
      this.printDiv('budget-pdf');
    }
  }

  handleSearch = (selectedKeys: any[], confirm: () => void) => {
    confirm();
    this.setState({ searchText: selectedKeys[0] });
  };

  handleReset = (clearFilters: () => void) => {
    clearFilters();
    this.setState({ searchText: '' });
  };

  handleChange = (pagination, filters, sorter) => {
    this.setState({
      filteredInfo: filters,
      sortedInfo: sorter,
    });
  };

  private clearAllFilters = () => {
    this.setState({
      filteredInfo: {},
      sortedInfo: {},
      searchText: '',
    });
  };

  reactToPrintContent = () => {
    return this.componentToPrintRef;
  };

  onPrint = async (id) => {
    const graphQLClient = getClient();
    message.loading('Generando PDF');

    const response = await graphQLClient.query<
      GetBudgetQuery,
      GetBudgetQueryVariables
    >({
      query: GetBudgetDocument,
      variables: {
        id,
      },
      fetchPolicy: 'network-only',
    });

    this.setState(
      {
        selectedBudget: response.data?.budget,
        print: true,
      },
      // @ts-ignore
      () => {
        // @ts-ignore
        this.reactPrintRef.startPrint(this.componentToPrintRef);
      },
    );
  };

  render(): React.ReactNode {
    let { sortedInfo, filteredInfo } = this.state;
    sortedInfo = sortedInfo || {};
    filteredInfo = filteredInfo || {};

    const IDColumn = {
      title: 'ID',
      dataIndex: 'readableId',
      key: 'id',
      width: 140,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any): React.ReactNode => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => {
              this.searchInput = node;
            }}
            placeholder={`Buscar por ID`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => this.handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => this.handleSearch(selectedKeys, confirm)}
            // @ts-ignore
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => this.handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </div>
      ),
      filterIcon: (filtered: any): React.ReactNode => (
        // @ts-ignore
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: string, record: any) => {
        return (record['readableId'] || '')
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible: any) => {
        if (visible) {
          setTimeout(() => this.searchInput.select());
        }
      },
      render: (text: string, record: BudgetInstance) => {
        const id = record.readableId;
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[this.state.searchText]}
            autoEscape
            textToHighlight={id}
          />
        );
      },
    };

    const statusColumn = {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (text: string, record: BudgetInstance) => {
        const matchingStatus = this.props.budgetsConfig?.status.find(
          (status) => status.code === record.status,
        );

        if (matchingStatus) {
          const text = capitalizeFirstChar(
            matchingStatus?.locale.find((locale) => locale.code === 'esES')
              ?.value,
          );

          if (record.status === 'accepted') {
            return (
              <Tag color={'green'} key={record.status}>
                {text}
              </Tag>
            );
          } else if (
            record.status === 'canceled' ||
            record.status === 'rejected'
          ) {
            return (
              <Tag color={'volcano'} key={record.status}>
                {text}
              </Tag>
            );
          } else {
            return <Tag key={record.status}>{text}</Tag>;
          }
        } else {
          return;
        }
      },
      filters: this.props.budgetsConfig?.status.map((status) => {
        return {
          value: status.code,
          text: capitalizeFirstChar(
            status.locale.find((locale) => locale.code === 'esES')?.value,
          ),
        };
      }),
      // filteredValue: (filteredInfo as any).status || null,
      onFilter: (filter: string, record: BudgetInstance) => {
        return record.status === filter;
      },
      sorter: (a: BudgetInstance, b: BudgetInstance) =>
        a.status.length - b.status.length,
      sortOrder:
        (sortedInfo as any).columnKey === 'status' && (sortedInfo as any).order,
    };

    const creationDateColumn = {
      title: 'Fecha de alta',
      key: 'creation-date',
      width: 200,
      render: (text: string, record: BudgetInstance) => {
        return formatDate(moment(record.creationDate));
      },
      sortDirections: ['descend', 'ascend'] as SortOrder[],
      defaultSortOrder: 'ascend' as 'descend' | 'ascend' | undefined,
      sorter: (a: BudgetInstance, b: BudgetInstance) => {
        return a.creationDate.valueOf() - b.creationDate.valueOf();
      },
      sortOrder:
        (sortedInfo as any).columnKey === 'creation-date' &&
        (sortedInfo as any).order,
    };

    const referenceColumn = {
      title: 'Referencia',
      dataIndex: 'reference',
      key: 'reference',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any): React.ReactNode => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => {
              this.searchInput = node;
            }}
            placeholder={`Buscar por referencia`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => this.handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => this.handleSearch(selectedKeys, confirm)}
            // @ts-ignore
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => this.handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </div>
      ),
      filterIcon: (filtered: any): React.ReactNode => (
        // @ts-ignore
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: string, record: any) => {
        return (record['reference'] || '')
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible: any) => {
        if (visible) {
          setTimeout(() => this.searchInput.select());
        }
      },
      render: (text: string, record: BudgetInstance) => {
        return (
          record?.reference && (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={record.reference}
            />
          )
        );
      },
    };

    const clientColumn = {
      title: 'Cliente',
      dataIndex: 'client',
      key: 'client',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any): React.ReactNode => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => {
              this.searchInput = node;
            }}
            placeholder={`Buscar por cliente`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => this.handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => this.handleSearch(selectedKeys, confirm)}
            // @ts-ignore
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => this.handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </div>
      ),
      filterIcon: (filtered: any): React.ReactNode => (
        // @ts-ignore
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: string, record: BudgetInstance) => {
        const recordClientName = this.props.allClients.find(
          (client) => client.id === record.client,
        )?.name;

        return (recordClientName || '')
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible: any) => {
        if (visible) {
          setTimeout(() => this.searchInput.select());
        }
      },
      render: (text: string, record: BudgetInstance) => {
        const clientId = record.client;

        const clientName = this.props.allClients.find(
          (client) => client.id === clientId,
        )?.name;

        if (clientName) {
          return (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={clientName}
            />
          );
        }
      },
    };

    const companyColumn = {
      title: 'Empresa',
      key: 'company',
      render: (text: string, record: BudgetInstance) => {
        const matchingCompany = this.props.budgetsConfig?.companies.find(
          (company) => company.code === record.company,
        );

        return matchingCompany?.data.name;
      },
      filters: this.props.budgetsConfig?.companies.map((company) => {
        return {
          value: company.code,
          text: company.data.name,
        };
      }),
      // filteredValue: (filteredInfo as any).company || null,
      onFilter: (filter: string, record: BudgetInstance) => {
        return record.company === filter;
      },
      sorter: (a: BudgetInstance, b: BudgetInstance) =>
        a.company.length - b.company.length,
      sortOrder:
        (sortedInfo as any).columnKey === 'company' &&
        (sortedInfo as any).order,
    };

    const itinerariesColumn = {
      title: '# Itinerarios',
      key: 'number-itineraries',
      width: 125,
      render: (text: string, record: BudgetInstance) => {
        return record.itineraries.length || 0;
      },
      sorter: (a: BudgetInstance, b: BudgetInstance) => {
        return a.itineraries.length > b.itineraries.length
          ? 1
          : b.itineraries.length > a.itineraries.length
          ? -1
          : 0;
      },
      sortOrder:
        (sortedInfo as any).columnKey === 'number-itineraries' &&
        (sortedInfo as any).order,
    };

    const totalColumn = {
      title: 'Total (con I.V.A)',
      key: 'total',
      render: (text: string, record: BudgetInstance) => {
        return (
          <b>{`${computeBudgetTotalWithVAT(
            // @ts-ignore
            instantiateBudgetFromNetworkResponse(record),
          ).toFixed(2)} €`}</b>
        );
      },
      sorter: (a: BudgetInstance, b: BudgetInstance) => {
        const totalA = computeBudgetTotalWithVAT(
          // @ts-ignore
          instantiateBudgetFromNetworkResponse(a),
        );

        const totalB = computeBudgetTotalWithVAT(
          // @ts-ignore
          instantiateBudgetFromNetworkResponse(b),
        );

        return totalA - totalB;
      },
      sortOrder:
        (sortedInfo as any).columnKey === 'total' && (sortedInfo as any).order,
    };

    const actionsColumn = {
      title: 'Acciones',
      key: 'actions',
      width: 155,
      render: (text: string, record: BudgetInstance) => {
        return (
          <Space direction="horizontal">
            <Button
              size="small"
              onClick={() => this.openEditBudgetDrawer(record.id)}
            >
              Gestionar
            </Button>
            <Button
              loading={this.state.print}
              size="small"
              onClick={() => {
                this.onPrint(record.id);
              }}
            >
              Generar PDF
            </Button>
            <Popconfirm
              title="¿Estás seguro de que deseas eliminar este presupuesto?"
              onConfirm={() => {
                this.props.deleteBudget(record.id);
              }}
            >
              <Button danger size="small">
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    };

    const adminColumns = [
      IDColumn,
      statusColumn,
      creationDateColumn,
      referenceColumn,
      clientColumn,
      companyColumn,
      itinerariesColumn,
      totalColumn,
      actionsColumn,
    ];
    const clientColumns = [
      IDColumn,
      statusColumn,
      creationDateColumn,
      referenceColumn,
      itinerariesColumn,
      totalColumn,
      actionsColumn,
    ];

    const columns = this.props.isAdmin
      ? adminColumns
      : this.props.isClient
      ? clientColumns
      : [];

    return (
      <div>
        <ReactToPrint
          ref={(ref) => {
            this.reactPrintRef = ref;
          }}
          onAfterPrint={() => {
            this.setState({
              selectedBudget: undefined,
              print: false,
            });
          }}
          documentTitle={`presupuesto-${
            this.props.budgets.find((b) => b.id === this.state.selectedBudgetId)
              ?.readableId
          }`}
          removeAfterPrint
          content={this.reactToPrintContent}
        >
          <CreateBudget
            draft={true}
            refetchBudgets={this.props.refetchBudgets}
            visible={this.state.createBudgetDrawerVisible}
            onClose={this.onCreateBudgetDrawerCloseClicked}
          />
          <EditBudget
            draft={false}
            budgetId={this.state.selectedBudgetId}
            refetchBudgets={this.props.refetchBudgets}
            visible={this.state.editBudgetDrawerVisible}
            onClose={this.onEditBudgetDrawerCloseClicked}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'baseline',
              justifyContent: 'flex-start',
              paddingBottom: '2em',
            }}
          >
            <Button
              icon={<ReloadOutlined translate={undefined} />}
              style={{ marginRight: '10px' }}
              onClick={() => {
                this.props.refetchBudgets();
              }}
            >
              Refrescar
            </Button>
            <Button
              icon={<LegacyIcon type={'close'} />}
              onClick={this.clearAllFilters}
            >
              Eliminar filtros
            </Button>
            <div
              style={{
                flexDirection: 'row',
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {/*Right icons*/}
              <Button
                icon={<LegacyIcon type={'plus'} />}
                type={'primary'}
                onClick={this.openCreateBudgetDrawer}
              >
                Crear nuevo presupuesto
              </Button>
            </div>
          </div>
          <Table
            // onRow={(record, index) => {
            //   return {
            //     onClick: (e) => {
            //       e.preventDefault();

            //       this.openEditBudgetDrawer(record.id);
            //     },
            //   };
            // }}
            loading={this.props.loading}
            locale={{
              emptyText: (
                <Empty
                  description="No hay presupuestos disponibles"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            rowKey={(record) => record.id}
            size={'middle'}
            pagination={{ pageSize: 25 }}
            // scroll={{ y: 'calc(50vh)' }}
            columns={columns}
            dataSource={this.props.budgets}
            onChange={this.handleChange}
          />
        </ReactToPrint>
        <CreateBudgetPDF
          budget={this.state.selectedBudget}
          ref={this.setComponentRef}
        />
      </div>
    );
  }
}

export default Budgets;
